import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { removeTagsFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { buildEmdCompositeForExport } from '@aytm/app/stats/page/question-views/emd/components/chart/lib/buildEmdCompositeForExport'
import {
  renderCircle,
  renderTimeslice,
  updateCircle,
  updateTimeslice
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/emdLineConfig'

import Heatmap from '@aytm/app/stats/page/question-views/emd/components/Heatmap'
import Windmap from '@aytm/app/stats/page/question-views/emd/components/Windmap'

import ChartContainer from '@aytm/app/stats/page/question-views/components/charts/highcharts/ChartContainer'

// Module-level constant: passing margin as a prop would allocate a new array
// per coffee renderReactChart call and invalidate useEffect deps downstream.
const MARGIN = [20, 50, 20, 50]

// Windmap trails accumulate at 25fps; one full MAX_PARTICLE_AGE cycle (50 frames)
// is the point where the canvas reaches its live-page steady state. The headless
// snapshot waits it out — legacy exported a canvas that had been animating for ages.
const WINDMAP_SETTLE_MS = 2000

const calcTimeVals = (answer, videoDuration) => {
  const bAnswer = answer?.emd?.b1
  if (!bAnswer) return []
  const timeVals = Object.keys(bAnswer.pointDensity ?? {})
    .map(el => Math.round(parseFloat(el) * 10000) / 10000)
    .sort((a, b) => a - b)
  if (videoDuration > 0 && timeVals.length && videoDuration > timeVals[timeVals.length - 1]) {
    timeVals[timeVals.length - 1] = videoDuration
  }
  return timeVals
}

const buildLines = (answer, batches, videoDuration, questionId) => {
  if (!answer?.emd || !batches) return []
  const data = []

  batches.forEach((b, i) => {
    const bAnswer = answer.emd[`b${i + 1}`] || {}

    const mapXY = arr => (arr || []).map(el => ({ x: el[0], y: el[1] }))
    const precise = mapXY(bAnswer.meanLine)
    const smooth = mapXY(bAnswer.meanLineSmooth)
    const segmented = mapXY(bAnswer.segmentedLine)

    if (precise.length && precise[precise.length - 1].x < videoDuration) precise[precise.length - 1].x = videoDuration
    if (smooth.length && smooth[smooth.length - 1].x < videoDuration) smooth[smooth.length - 1].x = videoDuration
    if (segmented.length && segmented[segmented.length - 1].x < videoDuration)
      segmented[segmented.length - 1].x = videoDuration

    const segments = {}
    const changePoints = []
    const filtered = segmented.filter(
      (el, idx) =>
        0 <= segmented[idx].x &&
        0 <= segmented[idx].y &&
        (idx === 0 || idx === segmented.length - 1 || segmented[idx - 1].y !== el.y || segmented[idx + 1].y !== el.y)
    )
    for (let f = 0; f < filtered.length; f++) {
      const cur = filtered[f]
      const d = filtered.find(c => cur.x === c.x)
      const n = Math.floor(f / 2)
      if (d && d.x < bAnswer.nx && d.x > 0 && !changePoints.includes(d.x)) changePoints.push(d.x)
      segments[n] = segments[n] || []
      segments[n].push(cur)
    }

    const label1 = removeTagsFromText(b.text?.[0] || '')
    const label2 = removeTagsFromText(b.text?.[1] || '')

    const pointDensity = Object.entries(bAnswer.pointDensity || {}).sort((a, c) => parseFloat(a[0]) - parseFloat(c[0]))

    data.push({
      batch: i,
      border: `url(#outline-${questionId}-${i})`,
      gradient: `url(#batch-gradient-${questionId}-${i})`,
      color: b.colors,
      text: [label1, label2],
      precise,
      smooth,
      segmented,
      segments,
      changePoints,
      pointDensity,
      nx: bAnswer.nx
    })
  })

  return data
}

const getHeatmapData = answer => {
  const ab = answer?.emd?.b1
  if (!ab?.levels || !ab?.contours) return {}
  const levels = (ab.levels || []).filter((l, i, arr) => l === 0 || arr.indexOf(l) === i)
  return { contours: ab.contours || [], levels }
}

const getWindmapData = answer => {
  const ab = answer?.emd?.b1
  if (!ab) return {}
  return { nx: ab.nx, ny: ab.ny, dx: ab.dx, dy: ab.dy }
}

const EmotionalDialChart = ({
  questionId,
  questionKey,
  qType,
  viewMode,
  lineMode,
  answer,
  batches,
  width,
  height,
  videoDuration,
  videoTime,
  playing,
  onTimeUpdate,
  onPlayingEnd,
  colorTheme,
  features,
  onChartReady,
  videoEl,
  onTimeValsChange
}) => {
  // The <video> lives outside React (a sibling coffee view), and its duration arrives lazily
  // via `loadedmetadata`. Own that here rather than re-rendering from coffee: the resolved
  // duration is what every downstream calculation keys off.
  const [lazyDuration, setLazyDuration] = useState(0)

  useEffect(() => {
    const videoDomEl = videoEl?.querySelector?.('video')
    if (!videoDomEl) return

    const onMeta = () => setLazyDuration(videoDomEl.duration || 0)
    if (videoDomEl.duration) onMeta()

    videoDomEl.addEventListener('loadedmetadata', onMeta)
    return () => videoDomEl.removeEventListener('loadedmetadata', onMeta)
  }, [videoEl])

  const resolvedDuration = lazyDuration || videoDuration

  const timeVals = useMemo(() => calcTimeVals(answer, resolvedDuration), [answer, resolvedDuration])

  // The video view snaps its scrubber to the nearest sample time and reads them off the
  // Backbone model, so hand them back rather than keeping a second copy in coffee.
  useEffect(() => {
    onTimeValsChange?.(timeVals)
  }, [timeVals, onTimeValsChange])
  const linesAll = useMemo(
    () => buildLines(answer, batches, resolvedDuration, questionId),
    [answer, batches, resolvedDuration, questionId]
  )
  const heatmap = useMemo(() => getHeatmapData(answer), [answer])
  const windmap = useMemo(() => getWindmapData(answer), [answer])

  // Render batch[0] only; `batches` still flows through builder so drawDefs
  // can create per-batch defs.
  const lines = linesAll[0] || null

  const timeslice = viewMode === 'timeslice'

  const resolvedVideoTime = useMemo(() => {
    if (!timeVals.length) return videoTime
    if (timeVals.includes(videoTime)) return videoTime
    return timeVals.reduce((p, c) => (Math.abs(c - videoTime) < Math.abs(p - videoTime) ? c : p))
  }, [timeVals, videoTime])

  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const apiRef = useRef({})
  const [chartId, setChartId] = useState(null)
  const handleMount = useCallback(chart => {
    chartRef.current = chart
    setChartId(chart.container.id)
  }, [])

  // Master's click contract, kept in refs so the handlers stay stable and a click does not
  // rebuild the chart options: while paused the cursor follows the mouse until a click pins it,
  // the next click moves it to the clicked sample and lets it follow again, and a click during
  // playback does nothing.
  const pinnedRef = useRef(false)
  const [pinned, setPinned] = useState(false)
  const playingRef = useRef(playing)
  const onTimeUpdateRef = useRef(onTimeUpdate)
  playingRef.current = playing
  onTimeUpdateRef.current = onTimeUpdate

  useEffect(() => {
    if (!playing) return
    pinnedRef.current = false
    setPinned(false)
  }, [playing])

  // A view-mode or line-mode switch releases the pin: on master either one rebuilt the chart, and
  // a fresh chart starts unpinned, following the mouse again.
  useEffect(() => {
    if (!pinnedRef.current) return
    pinnedRef.current = false
    setPinned(false)
    chartRef.current?.series[0]?.update({ enableMouseTracking: true })
  }, [viewMode, lineMode])

  const handleChartClick = useCallback(time => {
    if (playingRef.current) return
    pinnedRef.current = !pinnedRef.current
    setPinned(pinnedRef.current)
    // Straight on the series, as master's click did: an options update does not reach the
    // tracking of a series already drawn, and while tracking is on the chart's own hover keeps
    // moving the label to the mouse, away from the pinned marker.
    chartRef.current?.series[0]?.update({ enableMouseTracking: !pinnedRef.current })
    onTimeUpdateRef.current?.(time)
  }, [])

  // false tells the chart to skip its own tooltip refresh on that hover (see the emdline builder)
  const handleChartHover = useCallback(time => {
    if (playingRef.current || pinnedRef.current) return false
    onTimeUpdateRef.current?.(time)
  }, [])

  // Headless export (no-op on the live page). chartId is set by the builder's
  // load handler AFTER the timeslice/pointer marker is in the DOM, so it doubles
  // as the marker-readiness signal; Heatmap/Windmap mount in the same commit.
  // The composite (marker normalization + windmap/heatmap layers) is emd-owned —
  // ChartContainer's generic HC attachment is disabled via exportDelegated below.
  useExportableChart({
    deps: [chartId],
    prepareForExport: async () => {
      if (!timeslice) await new Promise(resolve => setTimeout(resolve, WINDMAP_SETTLE_MS))
      const chart = chartRef.current
      const composite = buildEmdCompositeForExport({ chart, container: containerRef.current })
      return [await svgToPngDataUrl(composite, chart.chartWidth, chart.chartHeight)]
    }
  })

  // PPTX bridge (pptx.coffee -> block.getCanvasFromSVG). Same composite the headless export
  // builds; coffee only rasterises it. Replaces the legacy EmdLine.formatSvgForExport.
  // A chart that is gone or not fully drawn gives the deck no picture, as master did: one question's
  // missing image must not fail the whole export, and the builder throws on a missing layer.
  apiRef.current.getExportSvg = () => {
    const chart = chartRef.current
    if (!chart?.container) return null

    try {
      return buildEmdCompositeForExport({ chart, container: containerRef.current })
    } catch {
      return null
    }
  }

  apiRef.current.getExportSize = () => {
    const chart = chartRef.current
    return chart?.container ? { width: chart.chartWidth, height: chart.chartHeight } : null
  }

  // The api object outlives the chart (the block keeps it across a switch to the map or no-chart
  // mode), so the reference must not keep pointing at a destroyed chart.
  useEffect(
    () => () => {
      chartRef.current = null
    },
    []
  )

  useEffect(() => {
    onChartReady?.(apiRef.current)
  }, [onChartReady])

  // Re-create the marker only when its type doesn't match the current mode.
  // Highcharts `events.load` fires once per chart instance, so a viewMode
  // switch (which triggers chart.update, not load) needs this fallback.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !lines) return

    const chartId = chart.container.id
    const hasTimeslice = Boolean(document.querySelector(`#${chartId} .highcharts-timeslice`))
    const hasPointer = Boolean(document.querySelector(`#${chartId} .highcharts-pointer`))

    if (timeslice && hasTimeslice) return
    if (!timeslice && hasPointer) return

    document.querySelector(`#${chartId} .highcharts-timeslice`)?.remove()
    document.querySelector(`#${chartId} .highcharts-pointer`)?.remove()

    if (timeslice) {
      renderTimeslice(chart, { time: resolvedVideoTime, animationTime: 0, lines, height, margin: MARGIN })
      updateTimeslice(chart, { time: resolvedVideoTime, animationTime: 0, lines })
    } else {
      renderCircle(chart, { time: resolvedVideoTime, lines })
      updateCircle(chart, { time: resolvedVideoTime, animationTime: 0, lines })
    }
    // `resolvedVideoTime` left out of deps deliberately — the effect below owns
    // tick-by-tick position updates; including it would tear down the marker
    // on every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeslice, lines, height])

  // Runs after every render, not on a dependency list: the chart options are rebuilt each render,
  // and each update the chart applies (in a layout effect, before this one) drops the marker's
  // label, so the label has to be put back beside the marker every time -- not only when the time,
  // the line mode (which `lines` does not reflect: it carries every mode at once) or the pin moves.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart || !lines) return
    if (timeslice) {
      updateTimeslice(chart, { time: resolvedVideoTime, animationTime: 0, lines })
    } else {
      updateCircle(chart, { time: resolvedVideoTime, animationTime: 0, lines })
    }
  })

  // `resolvedVideoTime` left out of deps deliberately — the interval drives
  // it; including it would restart the interval on every tick.
  useEffect(() => {
    if (!playing || !timeVals.length || !lines) return
    const chart = chartRef.current
    if (!chart) return

    const videoLength = timeVals[timeVals.length - 1]
    const pointsAmount = timeVals.length
    const animationIntervalSec = (videoLength - 0.2) / pointsAmount
    const animationIntervalMs = animationIntervalSec * 1000

    let pointIndex = timeVals.indexOf(resolvedVideoTime)
    if (pointIndex < 0) pointIndex = 0

    chart.series[0]?.update({ enableMouseTracking: false })

    const id = setInterval(() => {
      pointIndex += 1
      const next = timeVals[pointIndex]
      if (next == null) {
        pointIndex = 0
        onTimeUpdate?.(0, 0)
        onPlayingEnd?.()
        return
      }
      if (timeslice) {
        updateTimeslice(chart, { time: next, animationTime: animationIntervalSec, lines })
      } else {
        updateCircle(chart, { time: next, animationTime: animationIntervalSec, lines })
      }
      onTimeUpdate?.(next, animationIntervalSec)
    }, animationIntervalMs)

    return () => {
      clearInterval(id)
      chart.series[0]?.update({ enableMouseTracking: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, timeVals, lines, timeslice, onTimeUpdate, onPlayingEnd])

  const innerWidth = width - MARGIN[1] * 2
  const innerHeight = height - MARGIN[0] * 2

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {chartId && !timeslice && (
        <>
          <Heatmap
            questionId={questionId}
            chartId={chartId}
            heatmap={heatmap}
            width={width}
            height={height}
            margin={MARGIN}
            videoDuration={resolvedDuration}
            visible={!timeslice}
          />
          <Windmap
            chartId={chartId}
            windmap={windmap}
            width={innerWidth}
            height={innerHeight}
            margin={MARGIN}
            videoDuration={resolvedDuration}
            visible={!timeslice}
          />
        </>
      )}
      <ChartContainer
        questionId={questionId}
        questionKey={questionKey}
        qType={qType}
        chartMode="emdline"
        chartModeTypes={[]}
        lines={lines}
        batches={batches}
        heatmap={heatmap}
        windmap={windmap}
        timeslice={timeslice}
        viewMode={viewMode}
        lineMode={lineMode}
        height={height}
        margin={MARGIN}
        videoDuration={resolvedDuration}
        timeVals={timeVals}
        videoTime={resolvedVideoTime}
        exportDelegated
        onTimeUpdate={handleChartClick}
        colorTheme={colorTheme}
        features={features}
        onMount={handleMount}
        onMouseOver={handleChartHover}
        mouseTracking={!pinned}
      />
    </div>
  )
}

EmotionalDialChart.propTypes = {
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  questionKey: PropTypes.string,
  qType: PropTypes.string.isRequired,
  viewMode: PropTypes.string.isRequired,
  lineMode: PropTypes.string.isRequired,
  // the loading sentinel is a string, and the element is built before the placeholder decides
  // whether to draw it, so the sentinel reaches the declaration even though the chart never mounts
  answer: answerPropType,
  batches: PropTypes.array,
  width: PropTypes.number,
  height: PropTypes.number,
  videoDuration: PropTypes.number,
  videoEl: PropTypes.object,
  onTimeValsChange: PropTypes.func,
  videoTime: PropTypes.number,
  playing: PropTypes.bool,
  onTimeUpdate: PropTypes.func,
  onPlayingEnd: PropTypes.func,
  colorTheme: PropTypes.array,
  features: PropTypes.object,
  onChartReady: PropTypes.func
}

EmotionalDialChart.defaultProps = {
  videoDuration: 0,
  videoTime: 0,
  playing: false
}

EmotionalDialChart.displayName = 'EmotionalDialChart'

export default EmotionalDialChart
